import { ExpandMoreRounded as ExpandMoreRoundedIcon } from 'components/icons';
import StatusChip from 'components/@extended/StatusChip';
import { LabelCard } from 'components/cards/LabelCard';
import { dmsLinkValue } from 'components/dmslink/DmsLink';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import GridItemList, { infoLabel, Row } from 'components/GridItemList';
import InfoTooltip from 'components/InfoTooltip';
import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import React, { useCallback, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { StatCard } from 'sections/stats/Stats';
import { ClassifierData } from 'types/classifiers';
import { ContactDto, PartyType, RelationshipTypeCode } from 'types/contacts';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { getBlockingPolicyReadonlyState, getOverallStatusChipType } from '../blocking/BlockingPolicyReadonlyShared';
import {
    BlockingAmlTimecardsReadonlySimple,
    BlockingCasesReadonlySimple,
    BlockingTimecardsReadonlySimple,
} from '../blocking/BlockingPolicyReadonlySimple';
import { classifierChips, ContactSectionId } from '../contactFormMapping';
import { ContactSection } from '../form-sections/ContactSection';
import { ComplianceSummary } from './ComplianceView';

const yes = <FormattedMessage id="yes" />;
const no = <FormattedMessage id="no" />;

const CARD_CLASS =
    'border-divider bg-paper overflow-hidden rounded-[10px] border new:border-0 new:shadow-card new:outline-hidden';
const CARD_HEADING_CLASS = 'border-divider border-b px-4 py-3 text-base leading-6 font-semibold';

interface Props {
    contact: ContactDto;
    classifiers: Record<string, ClassifierData>;
}

const OtherView: React.FC<Props> = ({ contact, classifiers }) => {
    const missingFields = useContext(DraftActionsContext)?.draft?.missingFields;
    const isNew = useIsNewDesign();
    const getRelatedKeywords = useCallback(() => {
        if (!contact) return <div />;
        const items = (contact?.relatedKeywords || []).map((item, id) => (
            <StatusChip
                key={`keyword#${id}`}
                label={getClassifierText(classifiers['KEYWORD_TYPE'], item.keywordTypeCode)}
                type={'neutral'}
            />
        ));
        return <span className="flex flex-wrap gap-0.5">{items.length ? items : '-'}</span>;
    }, [contact, classifiers]);

    const relationshipGrowthKeyword = contact.relatedKeywords.find(
        (item) => item.keywordTypeCode === 'RELATIONSHIP_GROWTH'
    );
    // A person's keywords are read under Person details, where they are edited.
    const isKeywordInPersonSection = contact.partyType === PartyType.PERSON;

    const getCooperationAreaTypes = useMemo(() => {
        if (!contact || !contact.cooperationAreaTypes) return <div>-</div>;
        const items: React.ReactNode[] = [];
        contact.cooperationAreaTypes.forEach((item, index) => {
            const optionalComma = index < (contact.cooperationAreaTypes?.length ?? 0) - 1 ? ', ' : null;
            items.push(
                <React.Fragment key={item.cooperationAreaTypeCode}>
                    {getClassifierText(classifiers['COOPERATION_AREA_TYPE'], item.cooperationAreaTypeCode)}
                    {optionalComma}
                </React.Fragment>
            );
        });
        return <div>{items.length ? items : '-'}</div>;
    }, [contact, classifiers]);

    const data: Row[] = [
        {
            label: <FormattedMessage id="contacts.politically-exposed-person" />,
            content: contact.isPoliticallyExposedPerson ? yes : no,
            hidden: contact.partyType !== PartyType.PERSON,
        },
        // The new design's row of the form's DMS link field; classic leaves the link to the record's header.
        {
            label: <FormattedMessage id="contacts.dms-link" />,
            content: contact.dmsLink ? dmsLinkValue(contact.dmsLink) : '-',
            hidden: !isNew,
        },
        {
            content: contact?.notes ?? '-',
            label: <FormattedMessage id="contacts.other.notes" />,
            multiline: true,
        },
        {
            content: contact?.coiNotes ?? '-',
            label: <FormattedMessage id="contacts.other.coi-notes" />,
            multiline: true,
        },
        {
            content: getRelatedKeywords(),
            chips: classifierChips(
                classifiers['KEYWORD_TYPE'],
                contact.relatedKeywords?.map((item) => item.keywordTypeCode)
            ),
            label: <FormattedMessage id="contacts.related-keywords" />,
            hidden: isKeywordInPersonSection,
        },
        {
            hidden: !relationshipGrowthKeyword || isKeywordInPersonSection,
            content: relationshipGrowthKeyword?.notes ?? '-',
            label: <FormattedMessage id="contacts.reason-for-relationship-growth-potential" />,
            multiline: true,
        },
        {
            content: getCooperationAreaTypes,
            chips: classifierChips(
                classifiers['COOPERATION_AREA_TYPE'],
                contact.cooperationAreaTypes?.map((item) => item.cooperationAreaTypeCode)
            ),
            label: isNew ? (
                infoLabel(
                    'contacts.other.confirmed-cooperation-areas',
                    'contacts.other.tooltips.confirmed-cooperation-areas'
                )
            ) : (
                <FormattedMessage id="contacts.other.confirmed-cooperation-areas" />
            ),
        },
    ];

    const [casesPolicy, timecardsPolicy, amlTimecardsPolicy] = getBlockingPolicyReadonlyState(contact);
    const canBlockCases = useHasPermissions(['UPDATE.PARTIES.BLOCK_ADDING_NEW_CASES']);
    const canBlockTimecards = useHasPermissions(['UPDATE.PARTIES.BLOCK_ADDING_TIMECARDS']);

    const statuses = [
        {
            labelId: 'contacts.blocking.new-cases-widget',
            tooltipText: 'contacts.other.tooltips.block-adding-new-cases',
            policy: casesPolicy,
        },
        {
            labelId: 'contacts.blocking.timecards-widget',
            tooltipText: 'contacts.other.tooltips.block-adding-timecards',
            policy: timecardsPolicy,
        },
        {
            labelId: 'contacts.blocking.aml-timecards-widget',
            tooltipText: 'contacts.other.tooltips.block-adding-aml-timecards',
            policy: amlTimecardsPolicy,
        },
    ].map(({ labelId, tooltipText, policy }) => ({
        labelId,
        tooltipText,
        chip: (
            <StatusChip
                label={
                    policy.overallStatus === 'blocked'
                        ? t('contacts.blocking.status-blocked')
                        : t('contacts.blocking.status-not-blocked')
                }
                type={getOverallStatusChipType(policy.overallStatus)}
                className="new:leading-[0.875] text-base font-bold"
            />
        ),
    }));

    if (isNew) {
        // The server keeps a blocking axis as stored for a user without its privilege, so that card only reads.
        const blockingSection = (
            id: ContactSectionId,
            titleId: string,
            Readonly: React.FC<{ contact: ContactDto; card?: { titleId: string; button: React.ReactNode } }>,
            canEdit: boolean
        ) => (
            <ContactSection id={id} titleId={titleId} contact={contact} canEdit={canEdit}>
                {(edit) => <Readonly contact={contact} card={{ titleId, button: edit }} />}
            </ContactSection>
        );
        // Every card edits itself in place: the blocking axes and the history on the left, the rest of Other and
        // the blocking status on the right, as the General tab sets its sections beside its figures.
        return (
            <div className={RECORD_GRID_CLASS}>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {blockingSection(
                        'blocking-cases',
                        'contacts.blocking.new-cases',
                        BlockingCasesReadonlySimple,
                        canBlockCases
                    )}
                    {blockingSection(
                        'blocking-timecards',
                        'contacts.blocking.timecards',
                        BlockingTimecardsReadonlySimple,
                        canBlockTimecards
                    )}
                    {blockingSection(
                        'blocking-aml-timecards',
                        'contacts.blocking.aml-timecards',
                        BlockingAmlTimecardsReadonlySimple,
                        canBlockTimecards
                    )}
                    {/* A contact other than a client has no Compliance tab, so its compliance is edited here. */}
                    {contact.relationshipTypeCode !== RelationshipTypeCode.CLIENT ? (
                        <ContactSection id="compliance" titleId="contacts.compliance.title" contact={contact}>
                            {(edit) => <ComplianceSummary contact={contact} button={edit} />}
                        </ContactSection>
                    ) : null}
                    <Collapsible
                        render={
                            <section className="border-border bg-card new:border-0 new:shadow-card new:outline-hidden overflow-hidden rounded-[10px] border" />
                        }
                    >
                        <CollapsibleTrigger className="group flex w-full cursor-pointer items-center px-5 py-4 text-left">
                            <span className="text-section flex-1">
                                <FormattedMessage id="contacts.blocking-history" />
                            </span>
                            <ExpandMoreRoundedIcon className="text-muted-foreground transition-transform group-data-panel-open:rotate-180 motion-reduce:transition-none" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="h-(--collapsible-panel-height) overflow-hidden transition-[height] duration-250 ease-in-out data-ending-style:h-0 data-starting-style:h-0 motion-reduce:transition-none">
                            <p className="text-foreground m-0 px-5 pb-[18px] text-sm whitespace-pre-wrap">
                                {contact.blockingHistory ?? '-'}
                            </p>
                        </CollapsibleContent>
                    </Collapsible>
                </div>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    <ContactSection id="other" titleId="contacts.other.title" contact={contact} rows={data}>
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="contacts.other.title"
                                data={withMissing(data, missingFields)}
                                button={edit}
                            />
                        )}
                    </ContactSection>
                    {/* The blocking status on the case's figure cards, as the General tab draws its figures. */}
                    {statuses.map(({ labelId, tooltipText, chip }) => (
                        <StatCard key={labelId} className="rounded-[10px]" contentClassName="px-5 py-4">
                            <span className="flex items-center gap-1">
                                <FormattedMessage id={labelId} />
                                <InfoTooltip text={tooltipText} className="text-grey-600 h-[1rem] w-[1em]" />
                            </span>
                            <div className="mt-1.5">{chip}</div>
                        </StatCard>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-12">
            {/* Sidebar with status widgets */}
            <div className="flex flex-col gap-4 sm:col-span-2">
                {statuses.map(({ labelId, tooltipText, chip }) => (
                    <LabelCard key={labelId} labelId={labelId} tooltipText={tooltipText} content={chip} color="info" />
                ))}
            </div>

            <div className="grid grid-cols-1 content-start gap-6 sm:col-span-10 sm:grid-cols-2">
                {/* Block adding cases */}
                <div className="flex flex-col gap-6">
                    <section className={CARD_CLASS}>
                        <div className={CARD_HEADING_CLASS}>
                            <FormattedMessage id="contacts.blocking.new-cases" />
                        </div>
                        <BlockingCasesReadonlySimple contact={contact} />
                    </section>

                    <section className={CARD_CLASS}>
                        <div className={CARD_HEADING_CLASS}>
                            <FormattedMessage id="contacts.blocking.timecards" />
                        </div>
                        <BlockingTimecardsReadonlySimple contact={contact} />
                    </section>

                    <section className={CARD_CLASS}>
                        <div className={CARD_HEADING_CLASS}>
                            <FormattedMessage id="contacts.blocking.aml-timecards" />
                        </div>
                        <BlockingAmlTimecardsReadonlySimple contact={contact} />
                    </section>
                </div>

                {/* Other; `self-start` keeps the card at its own height beside the taller column. */}
                <GridItemList
                    titleId="contacts.other.title"
                    size={12}
                    data={withMissing(data, missingFields)}
                    className="self-start"
                    key="other-list"
                />

                {/* Blocking history - spans both columns */}
                <Collapsible render={<section className={cn(CARD_CLASS, 'sm:col-span-2')} />}>
                    <CollapsibleTrigger className="group flex w-full cursor-pointer items-center px-4 py-[5px] text-left">
                        <span className="my-3 ml-2 flex-1 text-base leading-6 font-semibold">
                            <FormattedMessage id="contacts.blocking-history" />
                        </span>
                        <ExpandMoreRoundedIcon className="text-grey-600 transition-transform group-data-panel-open:rotate-180 motion-reduce:transition-none" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="h-(--collapsible-panel-height) overflow-hidden transition-[height] duration-250 ease-in-out data-ending-style:h-0 data-starting-style:h-0 motion-reduce:transition-none">
                        <p className="text-fg border-divider border-t px-4 pt-3 pb-4 text-sm leading-[1.5715] whitespace-pre-wrap">
                            {contact.blockingHistory ?? '-'}
                        </p>
                    </CollapsibleContent>
                </Collapsible>
            </div>
        </div>
    );
};

export default OtherView;
